import React, { useLayoutEffect, useRef, useState } from "react"
import mermaid from "mermaid"

let mermaidInitialized = false

function initMermaid() {
    if (mermaidInitialized) return
    mermaidInitialized = true
    mermaid.initialize({ startOnLoad: false, securityLevel: "loose", theme: "default" })
}

function normalizeXyChartCode(code) {
    if (!code || typeof code !== "string") return code
    let trimmed = code.trim()
    if (!/^\s*xychart-beta\b/im.test(trimmed) && !/^\s*xychart\b/im.test(trimmed)) return code
    trimmed = trimmed.replace(/\bxychart-beta\b/gi, "xychart")
    trimmed = trimmed.replace(/\bseries\s+/gi, "line ")
    trimmed = trimmed.replace(
        /\by-axis\s+("[^"]*"|[^\s-]+)\s+(-?\d*\.?\d+)\s+(-?\d*\.?\d+)(\s*(?:\r?\n|$))/gim,
        "y-axis $1 $2 --> $3$4"
    )
    trimmed = trimmed.replace(
        /\b(\d{4})-(\d{2})-(\d{2})(?:\s+\d{2}:\d{2}(?::\d{2})?)?\b/g,
        (_, y, m, d) => `${d}.${m}.${y.slice(-2)}`
    )
    return trimmed
}

function isXyChartCode(code) {
    const t = code?.trim() || ""
    return /^\s*xychart\b/im.test(t) || /^\s*xychart-beta\b/im.test(t)
}

function parseMermaidBarChartData(code) {
    if (!code || typeof code !== "string") return null
    const trimmed = code.trim()
    if (!/\bbar\s*\[/im.test(trimmed)) return null
    const xAxisRegex = /\bx-axis\s*(?:"[^"]*"|'[^']*')?\s*\[\s*([\s\S]*?)\]/im
    const barRegex = /\bbar\s*(?:"[^"]*"|'[^']*')?\s*\[\s*([\s\S]*?)\]/im
    const xAxisMatch = trimmed.match(xAxisRegex)
    const barMatch = trimmed.match(barRegex)
    if (!barMatch) return null
    const parseArrayContent = (str) => {
        const s = str.trim()
        if (!s) return []
        const parts = s.split(",").map((p) => p.trim().replace(/^["']|["']$/g, ""))
        return parts.filter(Boolean)
    }
    const parseNumberArray = (str) => {
        const parts = parseArrayContent(str)
        return parts.map((p) => {
            const n = parseFloat(p)
            return Number.isFinite(n) ? n : 0
        })
    }
    const values = parseNumberArray(barMatch[1])
    let labels = []
    if (xAxisMatch) labels = parseArrayContent(xAxisMatch[1])
    if (labels.length !== values.length && values.length > 0) {
        labels = values.map((_, i) => `${i + 1}`)
    }
    return labels.length > 0 && values.length > 0 ? { labels, values } : null
}

function HorizontalBarChart({ labels, values }) {
    const max = Math.max(...values, 1)
    return (
        <div className="pp-ai-barchart">
            {labels.map((label, i) => (
                <div key={i} className="pp-ai-barchart-row">
                    <div className="pp-ai-barchart-bar" style={{ width: `${(values[i] / max) * 100}%` }} title={label}>
                        <span className="pp-ai-barchart-label">{label}</span>
                    </div>
                    <span className="pp-ai-barchart-value">{values[i]}</span>
                </div>
            ))}
        </div>
    )
}

function downloadMermaidAsImage(svgString, format) {
    const mime = format === "jpg" ? "image/jpeg" : "image/png"
    const ext = format === "jpg" ? "jpg" : "png"
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" })
    const url = URL.createObjectURL(svgBlob)
    const img = new Image()
    img.onload = () => {
        let w = img.naturalWidth || 800
        let h = img.naturalHeight || 600
        if (w === 0 || h === 0) {
            const vb = svgString.match(/viewBox=["']?([^"']+)["']?/)?.[1]
            if (vb) {
                const parts = vb.split(/\s+/)
                if (parts.length >= 4) w = Math.ceil(Number(parts[2]) || 800)
                if (parts.length >= 4) h = Math.ceil(Number(parts[3]) || 600)
            }
        }
        const scale = 2
        w = Math.max(w * scale, 100)
        h = Math.max(h * scale, 100)
        const canvas = document.createElement("canvas")
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext("2d")
        if (format === "jpg") {
            ctx.fillStyle = "#fff"
            ctx.fillRect(0, 0, w, h)
        }
        ctx.drawImage(img, 0, 0, w, h)
        try {
            const dataUrl = canvas.toDataURL(mime, 0.95)
            URL.revokeObjectURL(url)
            const a = document.createElement("a")
            a.href = dataUrl
            a.download = `mermaid-diagram.${ext}`
            a.click()
        } catch (e) {
            URL.revokeObjectURL(url)
        }
    }
    img.onerror = () => URL.revokeObjectURL(url)
    img.src = url
}

function MermaidDiagram({ code }) {
    const barData = parseMermaidBarChartData(code)
    const containerRef = useRef(null)
    const idRef = useRef("mermaid-" + Math.random().toString(36).slice(2, 11))
    const [svg, setSvg] = useState(null)
    const [error, setError] = useState(false)
    const isXyChart = isXyChartCode(code)
    useLayoutEffect(() => {
        if (!code?.trim()) return
        initMermaid()
        const id = idRef.current
        const normalized = normalizeXyChartCode(code.trim())
        mermaid
            .render(id, normalized)
            .then(({ svg: svgContent }) => {
                setSvg(svgContent)
                setError(false)
            })
            .catch(() => {
                setError(true)
            })
    }, [code])
    if (barData) {
        return (
            <div className="pp-ai-mermaid-container">
                <HorizontalBarChart labels={barData.labels} values={barData.values} />
            </div>
        )
    }
    if (error) {
        return (
            <pre className="pp-ai-code">
                <code>{code}</code>
            </pre>
        )
    }
    if (svg) {
        const wrapClass = "pp-ai-mermaid-wrap" + (isXyChart ? " pp-ai-mermaid-wrap-xy" : "")
        return (
            <div className="pp-ai-mermaid-container">
                <div className={wrapClass} dangerouslySetInnerHTML={{ __html: svg }} />
                <div className="pp-ai-mermaid-download">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => downloadMermaidAsImage(svg, "png")}
                    >
                        PNG
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary ms-1"
                        onClick={() => downloadMermaidAsImage(svg, "jpg")}
                    >
                        JPG
                    </button>
                </div>
            </div>
        )
    }
    const wrapClass = "pp-ai-mermaid-wrap pp-ai-mermaid-loading" + (isXyChart ? " pp-ai-mermaid-wrap-xy" : "")
    return <div className={wrapClass} ref={containerRef} />
}

const MERMAID_DIRECTIVE =
    /^\s*(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|block-beta|mindmap|timeline|gitGraph|quadrantChart|xyChart|xychart-beta|requirement)\b/im

function looksLikeMermaid(code) {
    if (!code || typeof code !== "string") return false
    const head = code.trim().slice(0, 500)
    return MERMAID_DIRECTIVE.test(head)
}

export { initMermaid, MermaidDiagram, looksLikeMermaid }
